import { createJSONStorage } from "zustand/middleware";

/**
 * Web storage for zustand's `persist`, for caches that must never break the
 * app when the quota is full.
 *
 * These stores cache whole API responses (the product mapping, the tag list).
 * Once localStorage fills up — it is shared with react-secure-storage, whose
 * AES values are far larger than their plaintext — every setItem throws, and
 * an uncaught QuotaExceededError from a background rehydrate takes the screen
 * down with "The quota has been exceeded."
 *
 * A cache that cannot be written is simply a cache miss: the store keeps its
 * in-memory value and refetches next session. One eviction of our own key is
 * attempted first, so a stale oversized entry cannot wedge the cache forever.
 */
const guard = (backing) => ({
  getItem: (name) => {
    try {
      return backing().getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      backing().setItem(name, value);
    } catch {
      try {
        backing().removeItem(name);
        backing().setItem(name, value);
      } catch {
        // Still no room — leave the cache cold rather than throw.
      }
    }
  },
  removeItem: (name) => {
    try {
      backing().removeItem(name);
    } catch {
      // Nothing to do; the entry is already unreachable.
    }
  },
});

export const safePersistStorage = createJSONStorage(() => guard(() => localStorage));

/**
 * The same guard over sessionStorage, for the stores that keep per-tab state
 * there. sessionStorage has its own quota and throws the same way, and
 * `getStorage: () => sessionStorage` — the form these stores used — is the
 * unguarded one. The serialisation is unchanged, so existing entries are read
 * back exactly as before.
 */
export const safeSessionPersistStorage = createJSONStorage(() => guard(() => sessionStorage));
